import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Optional help enhancement. Links work without JavaScript; nothing is sent to Instagram.
 * @typedef {{copyButton?: HTMLButtonElement, copyStatus?: HTMLElement, copyFallback?: HTMLLabelElement, copyInput?: HTMLInputElement}} HelpRefs
 * @extends {Component<HelpRefs>}
 */
class MariposaDmHelp extends Component {
  /** @type {AbortController | undefined} */
  #variantController;
  #variantPending = false;
  #selectionRevision = 0;
  /** @type {string | null | undefined} Undefined means no update event has resolved yet. */
  #resolvedVariantId;

  connectedCallback() {
    super.connectedCallback();
    if (!this.dataset.productId) return;
    this.#variantController?.abort();
    this.#variantController = new AbortController();
    const { signal } = this.#variantController;
    const scope = this.closest('.shopify-section');
    scope?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    scope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.#updateCopyState();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateCopyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantController?.abort();
  }

  /** @param {import('@theme/events').VariantSelectedEvent} event */
  #onVariantSelected = (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLElement) || picker.dataset.productId !== this.dataset.productId) return;
    this.#selectionRevision += 1;
    this.#variantPending = true;
    this.#resolvedVariantId = undefined;
    if (this.refs.copyFallback) this.refs.copyFallback.hidden = true;
    this.#updateCopyState();
  };

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    if (String(event.detail.data.productId) !== this.dataset.productId) return;
    const id = String(event.detail.resource?.id ?? '');
    this.#resolvedVariantId = /^\d+$/.test(id) ? id : null;
    this.#variantPending = false;
    if (this.refs.copyStatus) this.refs.copyStatus.textContent = '';
    this.#updateCopyState();
  };

  #updateCopyState() {
    if (this.refs.copyButton) this.refs.copyButton.disabled = this.#variantPending;
    if (this.#variantPending && this.refs.copyStatus) {
      this.refs.copyStatus.textContent = this.dataset.variantPending ?? '';
    }
  }

  /** @returns {string | undefined} The current public variant ID, read when needed. */
  get selectedVariantId() {
    if (this.#variantPending) return;
    if (this.#resolvedVariantId !== undefined) return this.#resolvedVariantId ?? undefined;
    const productId = this.dataset.productId;
    if (!productId || !/^\d+$/.test(productId)) return;

    const section = this.closest('.shopify-section');
    const input = section?.querySelector(`product-form-component[data-product-id="${productId}"] input[name="id"]`);
    const variantId = input instanceof HTMLInputElement ? input.value : this.dataset.variantId;
    return variantId && /^\d+$/.test(variantId) ? variantId : undefined;
  }

  /** @returns {string | undefined} Clean product URL without query or visitor data. */
  get productLink() {
    if (!this.dataset.productUrl) return;
    try {
      const url = new URL(this.dataset.productUrl);
      if (url.protocol !== 'https:') return;
      url.search = '';
      url.hash = '';
      const variantId = this.selectedVariantId;
      if (variantId) url.searchParams.set('variant', variantId);
      return url.toString();
    } catch {
      return;
    }
  }

  /** Copies only the product link after an explicit user click. */
  async copyProductLink() {
    if (this.#variantPending) return;
    const selectionRevision = this.#selectionRevision;
    const link = this.productLink;
    const { copyStatus, copyFallback, copyInput } = this.refs;
    if (!link || !copyStatus || !copyFallback || !copyInput) return;

    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(link);
      if (!this.isConnected || selectionRevision !== this.#selectionRevision) return;
      copyFallback.hidden = true;
      copyStatus.textContent = this.dataset.copySuccess ?? '';
    } catch {
      if (!this.isConnected || selectionRevision !== this.#selectionRevision) return;
      copyInput.value = link;
      copyFallback.hidden = false;
      copyStatus.textContent = this.dataset.copyFallback ?? '';
      copyInput.focus();
      copyInput.select();
    }
  }

  /**
   * Publishes an optional custom event without blocking navigation.
   * @param {MouseEvent} event - Framework-routed link click.
   */
  trackHelp(event) {
    const target = event.target;
    if (!(target instanceof HTMLAnchorElement)) return;
    const channel = target.dataset.channel;
    if (channel !== 'instagram_dm' && channel !== 'instagram_profile') return;

    try {
      const shopify = window.Shopify;
      if (shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
      if (typeof shopify?.analytics?.publish !== 'function') return;

      const placement = ['product', 'cart', 'home'].includes(this.dataset.placement ?? '')
        ? this.dataset.placement ?? 'product'
        : 'product';
      /** @type {{channel: string, placement: string, product_id?: string, variant_id?: string}} */
      const data = { channel, placement };
      const productId = this.dataset.productId;
      if (productId && /^\d+$/.test(productId)) {
        data.product_id = productId;
        const variantId = this.selectedVariantId;
        if (variantId) data.variant_id = variantId;
      }

      const result = shopify.analytics.publish('mariposa_help_click', data);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch {
      // Analytics availability must never prevent the help link from opening.
    }
  }
}

if (!customElements.get('mariposa-dm-help')) {
  customElements.define('mariposa-dm-help', MariposaDmHelp);
}
